import { ref, get, set, push } from "firebase/database";
import { dbAsistencia as dbCampo } from "../firebase";
import { parseDMY } from "./asistenciaCampoService";

const LOGIN_PATH = "/api/auth/login";
const RECORDS_PATH = "/api/reports/work-summary/records";
const WORK_SUMMARY_PATH = "/api/reports/work-summary";
const COMMISSION_RECORDS_PATH = "/api/reports/work-summary/records/by-dni";
const COMMISSION_APPLY_SUGGESTIONS_PATH = "/api/reports/work-summary/records/by-dni/apply-suggestions";
const INCIDENCE_REGULARIZATIONS_PATH = "/api/attendance/incidence-regularizations";
const TRAVEL_RECORDS_PATH = "/api/reports/work-summary/records/by-dni-range-4h";
const LOGIN_TIMEOUT_MS = 30 * 1000;
const TRAVEL_REQUEST_TIMEOUT_MS = (() => {
  const v = Number(readFirstEnv(["REACT_APP_TRAVEL_TIMEOUT_MS", "VITE_TRAVEL_TIMEOUT_MS"], ""));
  return Number.isFinite(v) && v > 0 ? v : 90 * 1000;
})();
// Debe superar el peor caso (login + envio, x2 por el reintento ante 401) para no liberar el bloqueo
// mientras otra pestana sigue esperando
const TRAVEL_SENDING_LOCK_MS = 2 * (LOGIN_TIMEOUT_MS + TRAVEL_REQUEST_TIMEOUT_MS) + 60 * 1000;
const FALLBACK_CONFIG = {
  baseUrl: "https://asistencia-api.transformaciondigitalcusco.in",
  username: "wchihuantito",
  password: "gpainformaticagpa2026",
  deviceId: 1,
  tokenTtlMin: 45,
};

let tokenCache = {
  value: null,
  acquiredAt: 0,
  expiresAt: 0,
};

function readEnv(name, fallback = "") {
  let v;

  if (typeof process !== "undefined" && process?.env) {
    v = process.env[name];
  }

  if (
    (v == null || v === "") &&
    typeof import.meta !== "undefined" &&
    import.meta?.env
  ) {
    v = import.meta.env[name];
  }

  return typeof v === "string" && v.trim() ? v.trim() : fallback;
}

function readFirstEnv(names, fallback = "") {
  for (const name of names) {
    const v = readEnv(name, "");
    if (v) return v;
  }
  return fallback;
}

function getConfig() {
  const baseUrl =
    readFirstEnv([
    "REACT_APP_WORK_SUMMARY_BASE_URL",
    "VITE_WORK_SUMMARY_BASE_URL",
  ]) || FALLBACK_CONFIG.baseUrl;
  const username =
    readFirstEnv([
    "REACT_APP_WORK_SUMMARY_AUTH_USER",
    "VITE_WORK_SUMMARY_AUTH_USER",
  ]) || FALLBACK_CONFIG.username;
  const password =
    readFirstEnv([
    "REACT_APP_WORK_SUMMARY_AUTH_PASS",
    "VITE_WORK_SUMMARY_AUTH_PASS",
  ]) || FALLBACK_CONFIG.password;
  const deviceIdRaw = readFirstEnv(
    ["REACT_APP_WORK_SUMMARY_DEVICE_ID", "VITE_WORK_SUMMARY_DEVICE_ID"],
    String(FALLBACK_CONFIG.deviceId)
  );
  const tokenTtlMinRaw = readFirstEnv(
    ["REACT_APP_WORK_SUMMARY_TOKEN_TTL_MIN", "VITE_WORK_SUMMARY_TOKEN_TTL_MIN"],
    String(FALLBACK_CONFIG.tokenTtlMin)
  );

  const deviceId = Number(deviceIdRaw);
  const tokenTtlMin = Number(tokenTtlMinRaw);

  return {
    baseUrl,
    username,
    password,
    deviceId: Number.isFinite(deviceId) ? deviceId : 1,
    tokenTtlMin: Number.isFinite(tokenTtlMin) ? tokenTtlMin : 45,
  };
}

function codedError(code, message, extra = {}) {
  const err = new Error(message);
  err.code = code;
  Object.assign(err, extra);
  return err;
}

// fetch sin limite puede quedar colgado si el servidor acepta la conexion y nunca responde
async function fetchWithTimeout(url, options, timeoutMs) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } catch (err) {
    if (err?.name === "AbortError") {
      throw codedError("TIMEOUT", `El servidor no respondio en ${Math.round(timeoutMs / 1000)}s (${url}).`);
    }
    throw codedError("NETWORK", `No se pudo conectar con el servidor (${url}): ${err?.message || err}`);
  } finally {
    clearTimeout(timer);
  }
}

function normalizeBaseUrl(baseUrl) {
  return String(baseUrl || "").replace(/\/+$/, "");
}

export async function fetchWorkSummaryPunches({ dni, date }) {
  const cleanDni = String(dni || "").trim();
  const cleanDate = String(date || "").trim();
  if (!cleanDni || !/^\d{4}-\d{2}-\d{2}$/.test(cleanDate)) return null;

  const config = getConfig();
  const url = new URL(`${normalizeBaseUrl(config.baseUrl)}${WORK_SUMMARY_PATH}`);
  url.searchParams.set("dni", cleanDni);
  url.searchParams.set("date", cleanDate);
  const res = await authorizedFetch(config, url.toString());
  if (!res.ok) throw new Error(`Consulta de horarios falló (${res.status}).`);
  const payload = await res.json();
  const rows = [
    ...(Array.isArray(payload?.dailyRows) ? payload.dailyRows : []),
    ...(Array.isArray(payload?.incidenceRows) ? payload.incidenceRows : []),
  ];
  return rows.find((row) => String(row?.date || "").slice(0, 10) === cleanDate) || null;
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

function toSqlDateTime(dateObj) {
  if (!(dateObj instanceof Date) || Number.isNaN(dateObj.getTime())) return null;
  const yyyy = dateObj.getFullYear();
  const mm = pad2(dateObj.getMonth() + 1);
  const dd = pad2(dateObj.getDate());
  const hh = pad2(dateObj.getHours());
  const mi = pad2(dateObj.getMinutes());
  const ss = pad2(dateObj.getSeconds());
  return `${yyyy}-${mm}-${dd} ${hh}:${mi}:${ss}`;
}

function parseDateAndTimeParts(datePart, timePart) {
  const d = String(datePart || "").trim();
  const t = String(timePart || "").trim();
  if (!d || !t) return null;

  const tm = /^(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(t);
  if (!tm) return null;

  const hh = Number(tm[1]);
  const mi = Number(tm[2]);
  const ss = Number(tm[3] || 0);
  if (
    !Number.isFinite(hh) ||
    !Number.isFinite(mi) ||
    !Number.isFinite(ss) ||
    hh > 23 ||
    mi > 59 ||
    ss > 59
  ) {
    return null;
  }

  let yyyy;
  let mm;
  let dd;

  if (/^\d{4}-\d{2}-\d{2}$/.test(d)) {
    const parts = d.split("-").map((x) => Number(x));
    yyyy = parts[0];
    mm = parts[1];
    dd = parts[2];
  } else if (/^\d{2}[-/]\d{2}[-/]\d{4}$/.test(d)) {
    const parts = d.split(/[-/]/).map((x) => Number(x));
    dd = parts[0];
    mm = parts[1];
    yyyy = parts[2];
  } else {
    return null;
  }

  const dt = new Date(yyyy, mm - 1, dd, hh, mi, ss);
  if (Number.isNaN(dt.getTime())) return null;
  return toSqlDateTime(dt);
}

function toSqlDateFromDate(dateObj) {
  if (!(dateObj instanceof Date) || Number.isNaN(dateObj.getTime())) return null;
  const yyyy = dateObj.getFullYear();
  const mm = pad2(dateObj.getMonth() + 1);
  const dd = pad2(dateObj.getDate());
  return `${yyyy}-${mm}-${dd}`;
}

function resolveBaseDateForIncidence(row) {
  if (row?.desde instanceof Date && !Number.isNaN(row.desde.getTime())) return new Date(row.desde);
  if (row?.hasta instanceof Date && !Number.isNaN(row.hasta.getTime())) return new Date(row.hasta);
  const fromApplied = parseDMY(row?.appliedDate);
  if (fromApplied && !Number.isNaN(fromApplied.getTime())) return fromApplied;
  return null;
}

function extractDateTimeFromText(text) {
  const src = String(text || "");
  if (!src.trim()) return null;

  const patterns = [
    /(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}(?::\d{2})?)/,
    /(\d{2}-\d{2}-\d{4})[ T](\d{2}:\d{2}(?::\d{2})?)/,
    /(\d{2}\/\d{2}\/\d{4})[ T](\d{2}:\d{2}(?::\d{2})?)/,
  ];

  for (const re of patterns) {
    const m = re.exec(src);
    if (!m) continue;
    const normalized = parseDateAndTimeParts(m[1], m[2]);
    if (normalized) {
      return {
        dateTime: normalized,
        matchedText: m[0],
      };
    }
  }

  return null;
}

function extractObservationFromText(text, consumedDateTimeText) {
  const raw = String(text || "").trim();
  if (!raw) return null;

  const obsRegex = /\bobservaci[oó]n\b\s*[:=-]\s*(.+)$/i;
  const obsMatch = obsRegex.exec(raw);
  if (obsMatch && obsMatch[1]) return obsMatch[1].trim();

  const noDate = consumedDateTimeText
    ? raw.replace(consumedDateTimeText, " ")
    : raw;

  const cleaned = noDate
    .replace(/\bfecha\b\s*[:=-]\s*/gi, " ")
    .replace(/\bhora\b\s*[:=-]\s*/gi, " ")
    .replace(/\bobservaci[oó]n\b\s*[:=-]?\s*/gi, " ")
    .replace(/[|;,]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  return cleaned || null;
}

function extractTimeOnlyFromText(text) {
  const src = String(text || "");
  if (!src.trim()) return null;

  const timeRegexes = [
    /\ba\s+las\s+(\d{1,2}:\d{2}(?::\d{2})?)\b/i,
    /\b(\d{1,2}:\d{2}(?::\d{2})?)\b/,
  ];

  for (const re of timeRegexes) {
    const m = re.exec(src);
    if (!m?.[1]) continue;
    const t = m[1];
    const parts = t.split(":").map((x) => Number(x));
    const hh = parts[0];
    const mi = parts[1];
    const ss = parts.length > 2 ? parts[2] : 0;
    if (
      Number.isFinite(hh) &&
      Number.isFinite(mi) &&
      Number.isFinite(ss) &&
      hh >= 0 &&
      hh <= 23 &&
      mi >= 0 &&
      mi <= 59 &&
      ss >= 0 &&
      ss <= 59
    ) {
      return `${pad2(hh)}:${pad2(mi)}:${pad2(ss)}`;
    }
  }

  return null;
}

function extractObservationForOlvido(text) {
  const raw = String(text || "").trim();
  if (!raw) return null;

  const m = /^(.*?)\s+a\s+las\s+\d{1,2}:\d{2}(?::\d{2})?\b/i.exec(raw);
  if (m?.[1]) {
    const primary = m[1].replace(/[|;,]+$/g, "").trim();
    if (primary) return primary;
  }

  const beforeDash = raw.split("-")[0]?.trim();
  if (beforeDash) return beforeDash;

  return extractObservationFromText(raw, "");
}

function parsePayloadFromJustificacion({ justificacion, dni, baseDate }) {
  const text = String(justificacion || "").trim();
  if (!text) {
    throw new Error("No hay justificacion para extraer fecha_hora y observacion.");
  }

  const dt = extractDateTimeFromText(text);
  let fechaHora = dt?.dateTime || null;
  let consumedDateTimeText = dt?.matchedText || "";

  if (!fechaHora) {
    const dateSql = toSqlDateFromDate(baseDate);
    const timeOnly = extractTimeOnlyFromText(text);
    if (dateSql && timeOnly) {
      fechaHora = `${dateSql} ${timeOnly}`;
      consumedDateTimeText = timeOnly;
    }
  }

  if (!fechaHora) {
    throw new Error(
      "No se encontro fecha_hora valida en justificacion. Usa 'a las HH:MM' o fecha y hora completa."
    );
  }

  const observacion =
    extractObservationForOlvido(text) ||
    extractObservationFromText(text, consumedDateTimeText);
  if (!observacion) {
    throw new Error("No se encontro observacion en justificacion.");
  }

  return {
    dni: String(dni || "").trim(),
    fecha_hora: fechaHora,
    observacion,
  };
}

// Renueva el token antes de que venza para no enviar uno que expire en pleno request
const TOKEN_REFRESH_MARGIN_MS = 2 * 60 * 1000;

function decodeJwtExpMs(token) {
  try {
    const part = String(token).split(".")[1];
    if (!part) return null;
    const json = JSON.parse(atob(part.replace(/-/g, "+").replace(/_/g, "/")));
    return Number.isFinite(json?.exp) ? json.exp * 1000 : null;
  } catch {
    return null;
  }
}

function tokenIsFresh() {
  if (!tokenCache.value || !tokenCache.expiresAt) return false;
  return Date.now() < tokenCache.expiresAt - TOKEN_REFRESH_MARGIN_MS;
}

export function invalidateWorkSummaryToken() {
  tokenCache = { value: null, acquiredAt: 0, expiresAt: 0 };
}

// Comparte un mismo login entre llamadas concurrentes (aprobacion masiva, precarga, etc.)
let loginInFlight = null;

async function getBearerToken(config) {
  if (tokenIsFresh()) return tokenCache.value;
  if (!loginInFlight) {
    loginInFlight = loginAndCacheToken(config).finally(() => {
      loginInFlight = null;
    });
  }
  return loginInFlight;
}

async function loginAndCacheToken(config) {
  const loginUrl = `${normalizeBaseUrl(config.baseUrl)}${LOGIN_PATH}`;
  const res = await fetchWithTimeout(
    loginUrl,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        username: config.username,
        password: config.password,
      }),
    },
    LOGIN_TIMEOUT_MS
  );

  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw codedError("HTTP", `Login microservicio fallo (${res.status}): ${body}`, { status: res.status });
  }

  const data = await res.json().catch(() => ({}));
  const pickToken = (obj) =>
    obj?.token ||
    obj?.access_token ||
    obj?.accessToken ||
    obj?.jwt ||
    obj?.bearer ||
    null;

  let token =
    pickToken(data) || pickToken(data?.data) || pickToken(data?.result) || null;

  if (typeof token === "string") {
    token = token.replace(/^Bearer\s+/i, "").trim();
  }

  if (!token || typeof token !== "string") {
    const bodyPreview = JSON.stringify(data).slice(0, 400);
    throw new Error(
      `No se recibio bearer token en /api/auth/login. Respuesta: ${bodyPreview}`
    );
  }

  // Vencimiento real: exp del JWT o expires_in; si no hay, el TTL configurado
  const now = Date.now();
  const expiresInSec = Number(data?.expires_in ?? data?.expiresIn ?? data?.data?.expires_in ?? data?.data?.expiresIn);
  const candidates = [
    now + config.tokenTtlMin * 60 * 1000,
    decodeJwtExpMs(token),
    Number.isFinite(expiresInSec) && expiresInSec > 0 ? now + expiresInSec * 1000 : null,
  ].filter((x) => Number.isFinite(x));

  tokenCache = {
    value: token,
    acquiredAt: now,
    expiresAt: Math.min(...candidates),
  };

  return token;
}

// Precarga el token (al abrir la pantalla) para que la primera aprobacion no espere el login
export async function warmUpWorkSummaryToken() {
  return getBearerToken(getConfig());
}

// fetch autenticado: si la API responde 401/403 (token vencido o revocado), renueva el token y reintenta una vez.
// Es seguro reintentar porque el servidor rechazo la peticion sin procesarla.
async function authorizedFetch(config, url, options = {}, timeoutMs = TRAVEL_REQUEST_TIMEOUT_MS) {
  const doFetch = async () => {
    const token = await getBearerToken(config);
    return fetchWithTimeout(
      url,
      { ...options, headers: { ...(options.headers || {}), Authorization: `Bearer ${token}` } },
      timeoutMs
    );
  };

  const res = await doFetch();
  if (res.status !== 401 && res.status !== 403) return res;

  console.warn(`[workSummary] ${res.status} en ${url}; renovando token y reintentando una vez.`);
  invalidateWorkSummaryToken();
  return doFetch();
}

function buildEventKey({ userId, leaveId, payload }) {
  const base = [
    String(userId || "").trim(),
    String(leaveId || "").trim(),
    String(payload?.dni || "").trim(),
    String(payload?.fecha_hora || "").trim(),
    String(payload?.observacion || "").trim().toLowerCase(),
  ].join("|");
  return base;
}

function syncRef(userId, leaveId) {
  return ref(dbCampo, `integrations/workSummarySync/${userId}/${leaveId}`);
}

function incidenceSyncRef(userId, leaveId) {
  return ref(dbCampo, `integrations/incidenceRegularizationSync/${userId}/${leaveId}`);
}

async function readLastSync(userId, leaveId) {
  const snap = await get(syncRef(userId, leaveId));
  return snap.exists() ? snap.val() : null;
}

async function writeSyncState(userId, leaveId, data) {
  await set(syncRef(userId, leaveId), data);
}

async function readIncidenceSync(userId, leaveId) {
  const snap = await get(incidenceSyncRef(userId, leaveId));
  return snap.exists() ? snap.val() : null;
}

async function writeIncidenceSync(userId, leaveId, data) {
  await set(incidenceSyncRef(userId, leaveId), data);
}

function travelSyncRef(userId, leaveId) {
  return ref(dbCampo, `integrations/travelSync/${userId}/${leaveId}`);
}

async function readTravelSync(userId, leaveId) {
  const snap = await get(travelSyncRef(userId, leaveId));
  return snap.exists() ? snap.val() : null;
}

async function writeTravelSync(userId, leaveId, data) {
  await set(travelSyncRef(userId, leaveId), data);
}

function extractTimeRangeFromJustificacion(text) {
  const src = String(text || "").trim();
  if (!src) return null;

  // Pattern: "Desde HH:MM Hasta HH:MM" or "Desde HH:MM hasta HH:MM"
  const desdeHastaRegex = /desde\s+(\d{1,2}:\d{2}(?::\d{2})?)\s+hasta\s+(\d{1,2}:\d{2}(?::\d{2})?)/i;
  const m = desdeHastaRegex.exec(src);
  
  if (!m) return null;
  
  const time1 = m[1];
  const time2 = m[2];
  
  // Normalize time to HH:MM:SS format
  const normalizeTime = (t) => {
    const parts = t.split(":").map((x) => Number(x));
    const hh = pad2(parts[0]);
    const mi = pad2(parts[1] || 0);
    const ss = pad2(parts[2] || 0);
    return `${hh}:${mi}:${ss}`;
  };
  
  return {
    hora1: normalizeTime(time1),
    hora2: normalizeTime(time2),
  };
}

function parsePicoStatus(text) {
  const src = String(text || "").trim();
  if (!src) return { estado1: 0, estado2: 1 };

  // Pattern: "PICO:D0H1" or "PICO: D0H1"
  const picoRegex = /pico:\s*D(\d)H(\d)/i;
  const m = picoRegex.exec(src);

  if (!m) return { estado1: 0, estado2: 1 };

  return {
    estado1: Number(m[1]),
    estado2: Number(m[2]),
  };
}

function parseHorasSlots(text) {
  const src = String(text || "").trim();
  if (!src) return null;

  // Pattern: "HORAS:1:1:05:30,2:1:15:30,3:1:16:30,4:1:17:30"
  // Each group: slot:estado:hora
  const horasRegex = /horas:([0-9,:.]+)/i;
  const m = horasRegex.exec(src);

  if (!m) return null;

  const slotsData = m[1].split(',');
  const suggestions = [];

  for (const slotStr of slotsData) {
    const parts = slotStr.split(':');
    if (parts.length >= 3) {
      const slot = Number(parts[0]);
      const estado = Number(parts[1]);
      const hora = parts.slice(2).join(':'); // Rejoin in case hora has multiple colons

      if (Number.isFinite(slot) && Number.isFinite(estado) && hora) {
        // Normalize hora to HH:MM:SS
        const horaParts = hora.split(':').map(Number);
        const hh = pad2(horaParts[0] || 0);
        const mi = pad2(horaParts[1] || 0);
        const ss = pad2(horaParts[2] || 0);
        const normalizedHora = `${hh}:${mi}:${ss}`;

        suggestions.push({
          slot,
          estado,
          hora: normalizedHora,
        });
      }
    }
  }

  return suggestions.length > 0 ? suggestions : null;
}

function extractObservacionForCommission(text) {
  // Observaciones fijas segun requerimiento del API
  return {
    observacion1: "ENTRADA TURNO MANANA",
    observacion2: "SALIDA TURNO MANANA",
    observacion3: "ENTRADA TURNO TARDE",
  };
}

function parseCommissionPayloadFromJustificacion({ justificacion, dni, desde }) {
  const text = String(justificacion || "").trim();
  if (!text) {
    throw new Error("No hay justificacion para extraer datos de comision de servicio.");
  }

  const timeRange = extractTimeRangeFromJustificacion(text);
  if (!timeRange) {
    throw new Error(
      "No se encontro rango de horas valido en justificacion. Usa 'Desde HH:MM Hasta HH:MM'."
    );
  }

  const { estado1, estado2 } = parsePicoStatus(text);
  const observaciones = extractObservacionForCommission(text);

  const fecha = toSqlDateFromDate(desde instanceof Date ? desde : new Date());
  if (!fecha) {
    throw new Error("No se pudo obtener la fecha de la comision.");
  }

  return {
    dni: String(dni || "").trim(),
    fecha,
    hora1: timeRange.hora1,
    hora2: timeRange.hora2,
    estado1,
    estado2,
    observacion1: observaciones.observacion1,
    observacion2: observaciones.observacion2,
    observacion3: observaciones.observacion3,
  };
}

export function parseCommissionApplySuggestionsPayloadFromJustificacion({ justificacion, dni, desde }) {
  const text = String(justificacion || "").trim();
  if (!text) {
    throw new Error("No hay justificacion para extraer datos de comision de servicio.");
  }

  // Try to parse HORAS format first (more specific): HORAS:1:1:05:30,2:1:15:30,3:1:16:30,4:1:17:30
  const horasSlots = parseHorasSlots(text);

  const fecha = toSqlDateFromDate(desde instanceof Date ? desde : new Date());
  if (!fecha) {
    throw new Error("No se pudo obtener la fecha de la comision.");
  }

  let suggestions;
  let hora1;
  let hora2;

  if (horasSlots && horasSlots.length > 0) {
    // Use HORAS format: build suggestions from parsed slots
    suggestions = horasSlots.map(s => ({ slot: s.slot, estado: s.estado }));
    // Use first and last slot hours for hora1 and hora2
    hora1 = horasSlots[0]?.hora;
    hora2 = horasSlots[horasSlots.length - 1]?.hora;
  } else {
    // Fallback to Desde/Hasta format
    const timeRange = extractTimeRangeFromJustificacion(text);
    if (!timeRange) {
      throw new Error(
        "No se encontro rango de horas valido en justificacion. Usa 'Desde HH:MM Hasta HH:MM' o formato HORAS:..."
      );
    }
    hora1 = timeRange.hora1;
    hora2 = timeRange.hora2;

    const { estado1, estado2 } = parsePicoStatus(text);
    // Build suggestions array based on PICO pattern: D{estado1}H{estado2}
    // slot 1 usa estado1, slots 2-4 usan estado2
    suggestions = [
      { slot: 1, estado: estado1 },
      { slot: 2, estado: estado2 },
      { slot: 3, estado: estado2 },
      { slot: 4, estado: estado2 },
    ];
  }

  // Validate that both hours are present
  if (!hora1 || !hora2) {
    throw new Error(
      "La comisión de servicio debe tener hora de inicio y hora de fin. Formato: 'Desde HH:MM Hasta HH:MM' o 'HORAS:1:1:HH:MM,...'"
    );
  }

  return {
    dni: String(dni || "").trim(),
    fecha,
    hora1,
    hora2,
    deviceId: 1,
    suggestions,
  };
}

async function sendCommissionServiceRecord(payload, config) {
  const recordsUrl = `${normalizeBaseUrl(config.baseUrl)}${COMMISSION_APPLY_SUGGESTIONS_PATH}`;

  const res = await authorizedFetch(config, recordsUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new Error(
      `POST /work-summary/records/by-dni/apply-suggestions fallo (${res.status}): ${body}`
    );
  }

  return { success: true, payload };
}

async function sendIncidenceRegularizationRecord(payload, config) {
  const recordsUrl = `${normalizeBaseUrl(config.baseUrl)}${INCIDENCE_REGULARIZATIONS_PATH}`;

  const res = await authorizedFetch(config, recordsUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new Error(
      `POST /attendance/incidence-regularizations fallo (${res.status}): ${body}`
    );
  }

  return { success: true, payload };
}

export async function sendCommissionServiceRecordIfNeeded(row) {
  const tipoKey = String(row?.tipoKey || "").trim().toLowerCase();
  if (tipoKey !== "sc") {
    return { skipped: true, reason: "not_commission_service" };
  }

  const dni = String(row?.dni || "").trim();
  if (!dni) {
    throw new Error("No se puede enviar al microservicio: falta DNI.");
  }

  const config = getConfig();
  const payloadCore = parseCommissionApplySuggestionsPayloadFromJustificacion({
    justificacion: row?.justificacion,
    dni,
    desde: row?.desde instanceof Date ? row.desde : null,
  });

  const payload = {
    ...payloadCore,
    deviceId: config.deviceId,
  };

  const userId = row?.userId;
  const leaveId = row?.leaveId;

  if (!userId || !leaveId) {
    throw new Error("No se puede registrar: faltan userId/leaveId.");
  }

  try {
    await sendCommissionServiceRecord(payload, config);
    return { skipped: false, payload };
  } catch (err) {
    throw err;
  }
}

export async function sendWorkSummaryRecordIfNeeded(row) {
  const tipoKey = String(row?.tipoKey || "").trim().toLowerCase();
  if (tipoKey !== "op") {
    return { skipped: true, reason: "not_olvido_picar" };
  }

  const dni = String(row?.dni || "").trim();
  if (!dni) {
    throw new Error("No se puede enviar al microservicio: falta DNI.");
  }

  const config = getConfig();
  const payloadCore = parsePayloadFromJustificacion({
    justificacion: row?.justificacion,
    dni,
    baseDate: row?.desde instanceof Date ? row.desde : null,
  });

  const payload = {
    ...payloadCore,
    deviceId: config.deviceId,
  };

  const userId = row?.userId;
  const leaveId = row?.leaveId;
  const eventKey = buildEventKey({ userId, leaveId, payload });

  if (!userId || !leaveId) {
    throw new Error("No se puede registrar idempotencia: faltan userId/leaveId.");
  }

  const prev = await readLastSync(userId, leaveId);
  if (prev?.lastStatus === "sent" && prev?.eventKey === eventKey) {
    return { skipped: true, reason: "already_sent", payload };
  }

  await writeSyncState(userId, leaveId, {
    eventKey,
    lastStatus: "sending",
    lastAttemptAt: Date.now(),
    payload,
  });

  try {
    const recordsUrl = `${normalizeBaseUrl(config.baseUrl)}${RECORDS_PATH}`;

    const res = await authorizedFetch(config, recordsUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const body = await res.text().catch(() => "");
      throw new Error(
        `POST /work-summary/records fallo (${res.status}): ${body}`
      );
    }

    const responseBody = await res.text().catch(() => "");
    await writeSyncState(userId, leaveId, {
      eventKey,
      lastStatus: "sent",
      lastAttemptAt: Date.now(),
      sentAt: Date.now(),
      payload,
      responseStatus: res.status,
      responseBody,
    });

    return { skipped: false, payload };
  } catch (err) {
    await writeSyncState(userId, leaveId, {
      eventKey,
      lastStatus: "error",
      lastAttemptAt: Date.now(),
      payload,
      error: err?.message || "Error desconocido",
    });
    throw err;
  }
}

export function isTravelRow(row) {
  return String(row?.tipoKey || "").trim().toLowerCase() === "vi";
}

export function parseTravelPayload(row) {
  const dni = String(row?.dni || "").trim();
  if (!dni) {
    throw new Error("No se puede enviar al microservicio: falta DNI.");
  }

  const desde = row?.desde instanceof Date ? row.desde : null;
  const hasta = row?.hasta instanceof Date ? row.hasta : desde;
  const dateFrom = toSqlDateFromDate(desde);
  const dateTo = toSqlDateFromDate(hasta);
  if (!dateFrom || !dateTo) {
    throw new Error("El viaje debe tener fecha de inicio y fecha de fin validas.");
  }
  if (dateTo < dateFrom) {
    throw new Error("La fecha de fin del viaje es anterior a la fecha de inicio.");
  }

  return { dni, dateFrom, dateTo };
}

async function sendTravelRecord(payload, config, timing) {
  const loginStart = Date.now();
  await getBearerToken(config);
  timing.loginMs = Date.now() - loginStart;
  const recordsUrl = `${normalizeBaseUrl(config.baseUrl)}${TRAVEL_RECORDS_PATH}`;

  const requestStart = Date.now();
  const res = await authorizedFetch(
    config,
    recordsUrl,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
    TRAVEL_REQUEST_TIMEOUT_MS
  );

  const responseBody = await res.text().catch(() => "");
  timing.requestMs = Date.now() - requestStart;
  if (!res.ok) {
    throw codedError(
      "HTTP",
      `POST /work-summary/records/by-dni-range-4h fallo (${res.status}): ${responseBody}`,
      { status: res.status }
    );
  }

  return { status: res.status, responseBody };
}

function daysInRange(dateFrom, dateTo) {
  const from = new Date(`${dateFrom}T00:00:00`);
  const to = new Date(`${dateTo}T00:00:00`);
  return Math.round((to - from) / 86400000) + 1;
}

// Historial de tiempos de respuesta para calibrar TRAVEL_REQUEST_TIMEOUT_MS
async function logTravelTiming(entry) {
  console.info(
    `[viaje] ${entry.outcome} en ${entry.totalMs} ms (login ${entry.loginMs ?? "-"} ms, servidor ${entry.requestMs ?? "-"} ms, ${entry.days} dia(s))`
  );
  try {
    await push(ref(dbCampo, "integrations/travelTimings"), entry);
  } catch (err) {
    console.error("No se pudo guardar travelTimings:", err);
  }
}

// Evita envios concurrentes de la misma solicitud dentro de la sesion (doble click, aprobacion masiva, etc.)
const travelInFlight = new Map();

// Mensaje para el usuario final; el detalle tecnico queda en err.message / consola
export function travelErrorUserMessage(err) {
  switch (err?.code) {
    case "TIMEOUT":
      return "El servidor de asistencia tardó demasiado en responder.";
    case "NETWORK":
      return "No se pudo conectar con el servidor de asistencia. Puede estar caído o sin conexión a internet.";
    case "HTTP":
      return Number(err?.status) >= 500
        ? "El servidor de asistencia no está disponible en este momento."
        : `El servidor de asistencia rechazó la solicitud (código ${err.status}).`;
    case "IN_PROGRESS":
      return "Esta solicitud ya se está procesando desde otra pestaña o equipo. Espera unos minutos.";
    default:
      return err?.message || "Error desconocido.";
  }
}

export function sendTravelRecordIfNeeded(row) {
  if (!isTravelRow(row)) {
    return Promise.resolve({ skipped: true, reason: "not_travel" });
  }

  const userId = row?.userId;
  const leaveId = row?.leaveId;
  if (!userId || !leaveId) {
    return Promise.reject(new Error("No se puede registrar idempotencia: faltan userId/leaveId."));
  }

  const inFlightKey = `${userId}|${leaveId}`;
  if (travelInFlight.has(inFlightKey)) return travelInFlight.get(inFlightKey);

  const promise = doSendTravelRecord(row, userId, leaveId).finally(() => {
    travelInFlight.delete(inFlightKey);
  });
  travelInFlight.set(inFlightKey, promise);
  return promise;
}

async function doSendTravelRecord(row, userId, leaveId) {
  const config = getConfig();
  const payload = {
    ...parseTravelPayload(row),
    deviceId: config.deviceId,
  };
  const eventKey = [userId, leaveId, payload.dni, payload.dateFrom, payload.dateTo].join("|");

  const prev = await readTravelSync(userId, leaveId);
  if (prev?.eventKey === eventKey) {
    if (prev?.lastStatus === "sent") {
      return { skipped: true, reason: "already_sent", payload };
    }
    // Otro cliente/pestana podria estar enviando ahora mismo
    if (
      prev?.lastStatus === "sending" &&
      Date.now() - Number(prev?.lastAttemptAt || 0) < TRAVEL_SENDING_LOCK_MS
    ) {
      throw codedError("IN_PROGRESS", "El viaje ya se esta enviando al microservicio.");
    }
  }

  await writeTravelSync(userId, leaveId, {
    eventKey,
    lastStatus: "sending",
    lastAttemptAt: Date.now(),
    payload,
  });

  const startedAt = Date.now();
  const timing = { loginMs: null, requestMs: null };
  const timingEntry = (outcome, extra = {}) => ({
    userId,
    leaveId,
    dni: payload.dni,
    dateFrom: payload.dateFrom,
    dateTo: payload.dateTo,
    days: daysInRange(payload.dateFrom, payload.dateTo),
    outcome,
    startedAt,
    totalMs: Date.now() - startedAt,
    ...timing,
    timeoutMs: TRAVEL_REQUEST_TIMEOUT_MS,
    ...extra,
  });

  try {
    const { status, responseBody } = await sendTravelRecord(payload, config, timing);
    const entry = timingEntry("sent", { responseStatus: status });
    await writeTravelSync(userId, leaveId, {
      eventKey,
      lastStatus: "sent",
      lastAttemptAt: Date.now(),
      sentAt: Date.now(),
      payload,
      responseStatus: status,
      responseBody,
      timing: { loginMs: entry.loginMs, requestMs: entry.requestMs, totalMs: entry.totalMs },
    });
    await logTravelTiming(entry);
    return { skipped: false, payload };
  } catch (err) {
    const entry = timingEntry("error", { errorCode: err?.code || "UNKNOWN", responseStatus: err?.status ?? null });
    await writeTravelSync(userId, leaveId, {
      eventKey,
      lastStatus: "error",
      lastAttemptAt: Date.now(),
      payload,
      error: err?.message || "Error desconocido",
      errorCode: err?.code || "UNKNOWN",
      timing: { loginMs: entry.loginMs, requestMs: entry.requestMs, totalMs: entry.totalMs },
    }).catch(() => {});
    await logTravelTiming(entry);
    throw err;
  }
}

export async function sendIncidenceRegularizationRecordIfNeeded(row) {
  const tipoKey = String(row?.tipoKey || "").trim().toLowerCase();
  if (tipoKey !== "tc") {
    return { skipped: true, reason: "not_work_corrido" };
  }

  const dni = String(row?.dni || "").trim();
  if (!dni) {
    throw new Error("No se puede enviar al microservicio: falta DNI.");
  }

  const baseDate = resolveBaseDateForIncidence(row);
  const date = toSqlDateFromDate(baseDate);
  if (!date) {
    throw new Error("No se puede enviar al microservicio: falta la fecha de la solicitud.");
  }

  const config = getConfig();
  const payload = { dni, date };
  const userId = row?.userId;
  const leaveId = row?.leaveId;
  const eventKey = buildEventKey({ userId, leaveId, payload });

  if (!userId || !leaveId) {
    throw new Error("No se puede registrar idempotencia: faltan userId/leaveId.");
  }

  const prev = await readIncidenceSync(userId, leaveId);
  if (prev?.lastStatus === "sent" && prev?.eventKey === eventKey) {
    return { skipped: true, reason: "already_sent", payload };
  }

  await writeIncidenceSync(userId, leaveId, {
    eventKey,
    lastStatus: "sending",
    lastAttemptAt: Date.now(),
    payload,
  });

  try {
    await sendIncidenceRegularizationRecord(payload, config);
    await writeIncidenceSync(userId, leaveId, {
      eventKey,
      lastStatus: "sent",
      lastAttemptAt: Date.now(),
      sentAt: Date.now(),
      payload,
    });
    return { skipped: false, payload };
  } catch (err) {
    await writeIncidenceSync(userId, leaveId, {
      eventKey,
      lastStatus: "error",
      lastAttemptAt: Date.now(),
      payload,
      error: err?.message || "Error desconocido",
    });
    throw err;
  }
}
